import React, { useRef, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Paintbrush, Eraser, Undo2, Download, Save, Trash2, Palette } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const COLORS = [
  '#1C1C1E', '#FF6B6B', '#A2D2FF', '#B9FBC0', '#FFC8DD', '#CDB4DB',
  '#FDF0D5', '#F0C040', '#6BA3D6', '#A080B0', '#70C080', '#E090A0'
];

const BRUSH_SIZES = [2, 4, 8, 14, 22];

export default function DoodlePad() {
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [color, setColor] = useState('#1C1C1E');
  const [brushSize, setBrushSize] = useState(4);
  const [tool, setTool] = useState('pen'); // pen, eraser
  const [history, setHistory] = useState([]);
  const [savedDoodles, setSavedDoodles] = useState([]);
  const [title, setTitle] = useState('');
  const [showGallery, setShowGallery] = useState(false);

  const initCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    
    // Set canvas size
    const rect = canvas.parentElement.getBoundingClientRect();
    canvas.width = rect.width;
    canvas.height = 500;
    
    // Dot grid background
    ctx.fillStyle = '#FFFAF0';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    
    const dotSpacing = 20;
    ctx.fillStyle = '#D0D0D0';
    for (let x = dotSpacing; x < canvas.width; x += dotSpacing) {
      for (let y = dotSpacing; y < canvas.height; y += dotSpacing) {
        ctx.beginPath();
        ctx.arc(x, y, 1, 0, Math.PI * 2);
        ctx.fill();
      }
    }
    
    saveToHistory();
  }, []);

  useEffect(() => {
    initCanvas();
    fetchDoodles();
  }, [initCanvas]);

  const saveToHistory = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const data = canvas.toDataURL();
    setHistory(prev => [...prev.slice(-20), data]);
  };

  const fetchDoodles = async () => {
    try {
      const { data } = await axios.get(`${API}/doodles`, { withCredentials: true });
      setSavedDoodles(data);
    } catch (e) {
      console.error('Failed to fetch doodles:', e);
    }
  };

  const getPos = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    if (e.touches) {
      return { x: e.touches[0].clientX - rect.left, y: e.touches[0].clientY - rect.top };
    }
    return { x: e.clientX - rect.left, y: e.clientY - rect.top };
  };

  const startDraw = (e) => {
    e.preventDefault();
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const pos = getPos(e);
    
    ctx.beginPath();
    ctx.moveTo(pos.x, pos.y);
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.lineWidth = brushSize;
    
    if (tool === 'eraser') {
      ctx.globalCompositeOperation = 'destination-out';
    } else {
      ctx.globalCompositeOperation = 'source-over';
      ctx.strokeStyle = color;
    }
    
    setIsDrawing(true);
  };

  const draw = (e) => {
    e.preventDefault();
    if (!isDrawing) return;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const pos = getPos(e);
    
    ctx.lineTo(pos.x, pos.y);
    ctx.stroke();
  };

  const stopDraw = (e) => {
    if (e) e.preventDefault();
    if (!isDrawing) return;
    setIsDrawing(false);
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    ctx.globalCompositeOperation = 'source-over';
    saveToHistory();
  };

  const undo = () => {
    if (history.length < 2) return;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const prevState = history[history.length - 2];
    const img = new Image();
    img.onload = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(img, 0, 0);
    };
    img.src = prevState;
    setHistory(prev => prev.slice(0, -1));
  };

  const clearCanvas = () => {
    initCanvas();
  };

  const saveDoodle = async () => {
    if (!title.trim()) {
      toast.error('Give your doodle a name!');
      return;
    }
    const canvas = canvasRef.current;
    const dataUrl = canvas.toDataURL('image/png');
    
    try {
      await axios.post(`${API}/doodles`, {
        title: title.trim(),
        data_url: dataUrl
      }, { withCredentials: true });
      toast.success('Doodle saved! +5 creative points!');
      setTitle('');
      fetchDoodles();
    } catch (e) {
      toast.error('Failed to save doodle');
    }
  };

  const downloadDoodle = () => {
    const canvas = canvasRef.current;
    const link = document.createElement('a');
    link.download = `doodle-${Date.now()}.png`;
    link.href = canvas.toDataURL();
    link.click();
  };

  const deleteDoodle = async (doodleId) => {
    try {
      await axios.delete(`${API}/doodles/${doodleId}`, { withCredentials: true });
      toast.success('Doodle deleted');
      fetchDoodles();
    } catch (e) {
      toast.error('Failed to delete');
    }
  };

  return (
    <div className="space-y-4" data-testid="doodle-pad">
      {/* Toolbar */}
      <div className="neo-card p-4">
        <div className="flex flex-wrap items-center gap-3">
          {/* Tool Selection */}
          <div className="flex gap-1">
            <button
              onClick={() => setTool('pen')}
              className={`neo-btn p-2 ${tool === 'pen' ? 'bg-[#A2D2FF]' : 'bg-white'}`}
              data-testid="tool-pen"
              title="Pen"
            >
              <Paintbrush className="w-5 h-5" />
            </button>
            <button
              onClick={() => setTool('eraser')}
              className={`neo-btn p-2 ${tool === 'eraser' ? 'bg-[#FFC8DD]' : 'bg-white'}`}
              data-testid="tool-eraser"
              title="Eraser"
            >
              <Eraser className="w-5 h-5" />
            </button>
          </div>

          {/* Divider */}
          <div className="w-px h-8 bg-black" />

          {/* Colors */}
          <div className="flex gap-1.5 flex-wrap">
            {COLORS.map((c) => (
              <button
                key={c}
                onClick={() => { setColor(c); setTool('pen'); }}
                className={`w-7 h-7 rounded-full border-2 transition-transform ${
                  color === c && tool === 'pen' ? 'border-black scale-125' : 'border-gray-300'
                }`}
                style={{ backgroundColor: c }}
                data-testid={`color-${c.replace('#', '')}`}
              />
            ))}
          </div>

          <div className="w-px h-8 bg-black" />

          {/* Brush Sizes */}
          <div className="flex items-center gap-1.5">
            {BRUSH_SIZES.map((s) => (
              <button
                key={s}
                onClick={() => setBrushSize(s)}
                className={`rounded-full border-2 transition-transform flex items-center justify-center ${
                  brushSize === s ? 'border-black bg-gray-100' : 'border-gray-300 bg-white'
                }`}
                style={{ width: 28, height: 28 }}
                data-testid={`brush-${s}`}
              >
                <div className="rounded-full bg-black" style={{ width: Math.min(s, 16), height: Math.min(s, 16) }} />
              </button>
            ))}
          </div>

          <div className="w-px h-8 bg-black" />

          {/* Actions */}
          <div className="flex gap-1">
            <button onClick={undo} className="neo-btn p-2 bg-white" data-testid="undo-btn" title="Undo">
              <Undo2 className="w-5 h-5" />
            </button>
            <button onClick={clearCanvas} className="neo-btn p-2 bg-white" data-testid="clear-canvas-btn" title="Clear">
              <Trash2 className="w-5 h-5 text-red-500" />
            </button>
            <button onClick={downloadDoodle} className="neo-btn p-2 bg-white" data-testid="download-doodle-btn" title="Download">
              <Download className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>

      {/* Canvas */}
      <div className="neo-card overflow-hidden">
        <canvas
          ref={canvasRef}
          onMouseDown={startDraw}
          onMouseMove={draw}
          onMouseUp={stopDraw}
          onMouseLeave={stopDraw}
          onTouchStart={startDraw}
          onTouchMove={draw}
          onTouchEnd={stopDraw}
          className="w-full cursor-crosshair touch-none"
          data-testid="doodle-canvas"
          style={{ minHeight: 500 }}
        />
      </div>

      {/* Save */}
      <div className="flex gap-2">
        <input
          type="text"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          className="neo-input flex-1"
          placeholder="Name your doodle..."
          data-testid="doodle-title-input"
        />
        <button onClick={saveDoodle} className="neo-btn px-6 py-2 bg-[#B9FBC0] flex items-center gap-2" data-testid="save-doodle-btn">
          <Save className="w-5 h-5" />
          <span>Save</span>
        </button>
      </div>

      {/* Gallery Toggle */}
      <button
        onClick={() => setShowGallery(!showGallery)}
        className="neo-btn w-full py-3 bg-[#CDB4DB] flex items-center justify-center gap-2"
        data-testid="toggle-gallery-btn"
      >
        <Palette className="w-5 h-5" />
        <span>{showGallery ? 'Hide' : 'Show'} My Doodles ({savedDoodles.length})</span>
      </button>

      {/* Gallery */}
      {showGallery && savedDoodles.length > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          {savedDoodles.map((d) => (
            <div key={d.id} className="neo-card p-3">
              <img
                src={d.data_url}
                alt={d.title}
                className="w-full rounded-lg border border-gray-200 mb-2"
              />
              <div className="flex items-center justify-between">
                <p className="text-sm font-bold truncate">{d.title}</p>
                <button
                  onClick={() => deleteDoodle(d.id)}
                  className="p-1 hover:bg-red-100 rounded"
                  data-testid={`delete-doodle-${d.id}`}
                >
                  <Trash2 className="w-4 h-4 text-red-500" />
                </button>
              </div>
              <p className="text-xs text-gray-400">{d.date}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
